import React, { SyntheticEvent, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import { Redirect } from 'react-router-dom';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Notifier,
  notify,
  PageFooter,
  PageLoader,
} from '@scholastic/volume-react';
import { LoginApiRequest } from '../../state/Login.api';
import { ActionType, StoreContext } from '../../state/store';
import './Login.scss';

/**
 * This is Login function.
 * @exports
 * @returns JSX.Element
 */
export const Login = () => {
  const [userName, setUserName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { state, dispatch } = useContext(StoreContext);

  useEffect(() => {
    if (state && state.error && state.error !== null && state.error.status !== 401) {
      notify.warning('Something went wrong, please try again later');
      setIsLoading(false);
      dispatch({ type: ActionType.RESET_ERROR, data: null });
    }

    if (!state.isAuthenticated && Cookies.get('adminDetails')) {
      dispatch({ type: ActionType.LOGOUT, data: null });
    }
  }, [state]);

  if (state.isAuthenticated && Cookies.get('adminDetails')) {
    return <Redirect to="/home" />;
  }

  const handleLogin = async (e: SyntheticEvent<Element, Event>): Promise<void> => {
    e.preventDefault();
    if (userName?.trim() === undefined || userName?.trim() === '') {
      setIsValid(false);
    } else if (password?.trim() === undefined || password?.trim() === '') {
      setIsValid(false);
    } else {
      setIsValid(true);
      setIsLoading(true);

      const loginRequestObj: LoginApiRequest = {
        username: userName?.trim(),
        password: password?.trim(),
      };

      dispatch({ type: ActionType.LOGIN, data: loginRequestObj });
    }
  };

  if (state?.isAuthenticated) {
    Cookies.set('adminDetails', { adminid: userName?.trim(), adminp: password?.trim() });
    dispatch({ type: ActionType.GET_CUSTOMER_INFO, data: null });
    setIsLoading(false);
  } else {
    if (
      state?.customerInfo?.resultcode === 2 ||
      (state && state.error && state.error !== null && state.error.status === 401)
    ) {
      if (isLoading) setIsLoading(false);
      if (isValid) setIsValid(false);
    }
  }

  return (
    <div className="stats-login-container">
      <Notifier
        autoClose={5000}
        closeButton={false}
        closeOnClick={false}
        draggable
        draggablePercent={50}
        limit={1}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        position="top-center"
        role="alert"
      />

      {isLoading && <PageLoader color={'dark'} size={'large'} type={'border'} />}

      <div
        className={classNames(
          'stats-login-container__card',
          isLoading && 'stats-login-container__card--blur',
        )}
      >
        <div className="stats-login-container__header">Scholastic Statistical Reporting</div>
        <div
          className={
            'stats-login-container__body ' + (!isValid && 'stats-login-container__body--has-error')
          }
        >
          <p className="stats-login-container__body--intro">
            Welcome to the Stats System! <br />
            Start by entering your user name and password.
          </p>

          <Form
            className="stats-login-container__form container-fluid mt-3"
            tag="form"
            noValidate={true}
          >
            <FormGroup inline>
              <label className="vol-form__label col--small-2" htmlFor="username" id="usernameLabel">
                Username
              </label>
              <div className="col--small-5">
                <Input
                  aria-labelledby="usernameLabel"
                  id="username"
                  name="username"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                  invalid={!isValid}
                />
              </div>
            </FormGroup>
            <FormGroup inline>
              <label className="vol-form__label col--small-2" htmlFor="password" id="passwordLabel">
                Password
              </label>
              <div className="col--small-5">
                <Input
                  aria-labelledby="passwordLabel"
                  id="password"
                  name="password"
                  value={password}
                  type="password"
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  invalid={!isValid}
                />
              </div>
            </FormGroup>

            <FormGroup>
              <Button color="info" id="login" type="submit" onClick={handleLogin}>
                Login
              </Button>
            </FormGroup>
          </Form>

          <FormFeedback
            className="stats-login-container__body--description m-0"
            hidden={isValid}
            valid={isValid}
          >
            Login Failed: Please check your user name and password and try again!
          </FormFeedback>

          <p className="stats-login-container__body--description">
            <span>
              <strong className="stats-login-container__body--bold">Attention customers:</strong>
              COUNTER format reports are now available.
            </span>
            <br />
            <span>
              Upon sign-in, reports formatted as described in the COUNTER Code of Practice for Books
              and Reference Works are available under the COUNTER reports tab.
            </span>
          </p>
        </div>
        <div className="stats-login-container__bottom">
          <p>
            If you do not have your login credentials please call 1-888-326-6546 or email
            <a href="mailto:scholasticsupport@scholastic.com"> scholasticsupport@scholastic.com </a>
            for assistance.
          </p>
        </div>
      </div>
      <PageFooter className="stats-login-container__footer" />
    </div>
  );
};

export default Login;
